import { BellOutlined } from '@ant-design/icons';
import {
  Badge,
  Button,
  Col,
  Empty,
  List,
  Popover,
  Row,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { t } from 'helpers/i18n';
import { notificationHooks, notificationPermissionHooks } from 'hooks';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import NotificationItem from './NotificationItem';
import NotificationModal from './NotificationModal';
import './NotificationBell.scss';
import { StoreContext } from 'contexts';
import { notificationHelpers, trackingHelpers } from 'helpers';
import { serviceWorkerConstants, trackingConstants } from 'constants/index';
import { parseNotificationDeeplink } from './utils';

const { useNotifications } = notificationHooks;
const { useNotificationPermission } = notificationPermissionHooks;
const {
  installFirebaseAndUnsNotificationService,
  getNotificationConfig,
} = notificationHelpers;

const { COMMAND_NEW_NOTIFICATION } = serviceWorkerConstants;
const {
  TRACKING_CONTENT_REGION_NAME,
  TRACKING_CONTENT_NAME,
} = trackingConstants;

const NotificationBell: React.FC = () => {
  const { extraConfig } = useContext(StoreContext);

  const [visible, setVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const installedRef = useRef(false);

  const {
    permissionStatus,
    requestPermission,
    permissionModal,
  } = useNotificationPermission();

  const stnAdminAppId = getNotificationConfig(extraConfig, true)?.stnAppId;

  const {
    unreadCount,
    notifications,
    loading,
    fetchList,
    refreshUnread,
    markRead,
    markReadAll,
  } = useNotifications(stnAdminAppId);

  useEffect(() => {
    requestPermission(
      <Trans i18nKey="BrowserPermissions.Description.StaffNotification">
        Cần quyền truy cập&nbsp;
        <span className="text-primary text-medium">Thông báo</span>
        &nbsp;để nhận các cập nhật và thông báo mới nhất dành cho nhân viên
      </Trans>
    );
  }, [requestPermission]);

  useEffect(() => {
    if (permissionStatus !== 'granted' || installedRef.current) return;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) return;

    installedRef.current = true;
    installFirebaseAndUnsNotificationService(
      () => refreshUnread(),
      extraConfig,
      true
    );
  }, [permissionStatus, extraConfig, refreshUnread]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.command === COMMAND_NEW_NOTIFICATION) {
        refreshUnread();
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [refreshUnread]);

  const handleVisibleChange = (nextVisible: boolean) => {
    setVisible(nextVisible);
    if (nextVisible) {
      trackingHelpers.nonBlockTrackUserClickEvent(
        TRACKING_CONTENT_REGION_NAME.NotificationBell,
        TRACKING_CONTENT_NAME.OpenNotificationBell,
        JSON.stringify({ unreadCount })
      );
      fetchList();
      // The bell mounts once for the whole session and never remounts, so
      // opening the popover is the admin stream's re-sync touchpoint: the
      // helper re-checks the FCM token (throttled, no-op when unchanged) and
      // retries a previously failed install.
      if (
        permissionStatus === 'granted' &&
        stnAdminAppId &&
        'Notification' in window &&
        'serviceWorker' in navigator
      ) {
        installFirebaseAndUnsNotificationService(
          () => refreshUnread(),
          extraConfig,
          true
        );
      }
    }
  };

  const header = (
    <Row justify="space-between" align="middle" className="notification-header">
      <Col>
        <Typography.Text strong style={{ fontSize: 16 }}>
          {t('Notifications.Title')}
        </Typography.Text>
        {unreadCount > 0 && (
          <Tag color="red" style={{ marginLeft: 8 }}>
            {t('Notifications.NewCount', { count: unreadCount })}
          </Tag>
        )}
      </Col>
      <Col>
        <Typography.Link
          data-testid="notification-mark-all-read"
          data-track-content
          data-content-region-name={
            TRACKING_CONTENT_REGION_NAME.NotificationBell
          }
          data-content-name={TRACKING_CONTENT_NAME.MarkAllRead}
          data-content-payload={JSON.stringify({ unreadCount })}
          disabled={unreadCount === 0 || loading}
          onClick={markReadAll}
        >
          {t('Notifications.MarkAllAsRead')}
        </Typography.Link>
      </Col>
    </Row>
  );

  const renderBody = () => {
    if (loading) {
      return (
        <div className="notification-status">
          <Spin />
        </div>
      );
    }
    if (!notifications.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          className="notification-status"
        />
      );
    }
    return (
      <List
        className="notification-menu"
        dataSource={notifications}
        renderItem={notification => (
          <List.Item
            key={notification.id}
            className={notification.is_read ? undefined : 'unread'}
            data-track-content
            data-content-region-name={
              TRACKING_CONTENT_REGION_NAME.NotificationBell
            }
            data-content-name={TRACKING_CONTENT_NAME.NotificationItem}
            data-content-payload={JSON.stringify({
              id: notification.id,
              isRead: notification.is_read,
              hasDeeplink: !!parseNotificationDeeplink(notification),
            })}
            onClick={() => markRead(notification)}
          >
            <NotificationItem
              notification={notification}
              onMarkRead={markRead}
              trackingRegionName={TRACKING_CONTENT_REGION_NAME.NotificationBell}
            />
          </List.Item>
        )}
      />
    );
  };

  const content = (
    <div className="notification-dropdown">
      {header}
      {renderBody()}
      <div className="notification-footer py-half">
        <Button
          type="link"
          className="w-100"
          data-testid="notification-view-all"
          data-track-content
          data-content-region-name={
            TRACKING_CONTENT_REGION_NAME.NotificationBell
          }
          data-content-name={TRACKING_CONTENT_NAME.ViewAllNotification}
          data-content-payload={JSON.stringify({ count: notifications.length })}
          disabled={!notifications.length || loading}
          onClick={() => {
            setVisible(false);
            setModalOpen(true);
          }}
        >
          {t('Notifications.ViewAll')}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        overlayClassName="notification-popover"
        visible={visible}
        onVisibleChange={handleVisibleChange}
      >
        <span className="app-icon ml-base">
          <Badge count={unreadCount} overflowCount={99} size="small">
            <BellOutlined style={{ fontSize: 18 }} />
          </Badge>
        </span>
      </Popover>
      <NotificationModal
        open={modalOpen}
        appId={stnAdminAppId}
        unreadCount={unreadCount}
        onAfterChange={refreshUnread}
        onClose={() => setModalOpen(false)}
      />
      {permissionModal}
    </>
  );
};

export default NotificationBell;
